<template>
  <div class="bankbook-reports-modal" :class="{ 'is-page': page }">
    <LoadingOverlay :show="pdfLoading" :text="loadingText" />

    <div v-if="!page" class="reports-header">
      <div class="header-icon">
        <v-icon size="22" color="white">mdi-file-chart-outline</v-icon>
      </div>
      <div class="header-text">
        <h3>회계보고서류</h3>
        <p>{{ currentYear }}년 {{ currentMonth }}월{{ bizGubun ? ` · ${bizGubun}` : '' }}</p>
      </div>
      <div class="header-spacer"></div>
    </div>

    <div class="reports-body">
        <!-- 좌측: 보고서 목록 -->
        <aside class="reports-sidebar">
          <div class="sidebar-section-title">보고서</div>

          <button
            type="button"
            class="report-item report-item--primary"
            :class="{ active: isAnalysisWorkspace }"
            @click="selectReport('accounting-analysis')"
          >
            <v-icon size="16" class="report-icon">mdi-chart-timeline-variant</v-icon>
            <div class="report-text">
              <span class="report-title">회계분석보고서</span>
              <span class="report-desc">인건비·생계비·차입 점검</span>
            </div>
          </button>

          <button
            type="button"
            class="report-item report-item--primary"
            :class="{ active: isLedgerWorkspace }"
            @click="selectReport('ledger-book')"
          >
            <v-icon size="16" class="report-icon">mdi-book-open-variant</v-icon>
            <div class="report-text">
              <span class="report-title">장부</span>
              <span class="report-desc">표지·목차 + 보고서 이어붙임</span>
            </div>
          </button>

          <div v-if="isAnalysisWorkspace" class="period-setup">
            <div class="sidebar-section-title">분석 기간</div>
            <div class="period-year-row">
              <button type="button" class="ym-nav-btn" @click="changeReportYear(-1)">
                <v-icon size="16">mdi-chevron-left</v-icon>
              </button>
              <span class="period-year-value">{{ currentYear }}년</span>
              <button type="button" class="ym-nav-btn" @click="changeReportYear(1)">
                <v-icon size="16">mdi-chevron-right</v-icon>
              </button>
            </div>
            <div class="period-range-row">
              <label class="period-range-field">
                <span>시작</span>
                <select v-model.number="analysisStartMonth" @change="onRangeSelectChange">
                  <option
                    v-for="item in analysisMonthOptions"
                    :key="`start-${item.month}`"
                    :value="item.month"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </label>
              <span class="period-range-tilde">~</span>
              <label class="period-range-field">
                <span>종료</span>
                <select v-model.number="analysisEndMonth" @change="onRangeSelectChange">
                  <option
                    v-for="item in analysisMonthOptions"
                    :key="`end-${item.month}`"
                    :value="item.month"
                    :disabled="item.month < analysisStartMonth"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </label>
            </div>
            <div class="period-buttons period-buttons--months">
              <button
                v-for="item in analysisMonthOptions"
                :key="item.month"
                type="button"
                class="period-btn"
                :class="monthButtonClass(item.month)"
                @click="pickAnalysisMonth(item.month)"
              >
                {{ item.label }}
              </button>
            </div>
            <p class="period-hint">{{ analysisPeriodHint }} · 시작·종료를 고르거나 월을 두 번 눌러 기간 선택</p>
          </div>

          <div v-if="isAnalysisWorkspace" class="analysis-ledger-block">
            <div class="sidebar-section-title">참고</div>
            <p class="ledger-detail-hint">클릭하면 새 창에서 계정별내역을 엽니다.</p>
            <button
              type="button"
              class="report-item report-item--detail"
              :class="{ loading: referencePopupLoading }"
              :disabled="referencePopupLoading"
              @click="openAnalysisReferencePopup"
            >
              <v-icon size="15" class="report-icon">mdi-book-open-page-variant-outline</v-icon>
              <div class="report-text">
                <span class="report-title">계정별내역</span>
                <span class="report-desc">{{ referencePopupLoading ? '새 창으로 여는 중...' : '계정합계 · 전표 · 새 창' }}</span>
              </div>
              <v-icon v-if="!referencePopupLoading" size="14" class="report-open-icon">mdi-open-in-new</v-icon>
            </button>
          </div>

          <template v-if="isLedgerWorkspace">
            <div class="period-setup">
              <div class="sidebar-section-title">연월</div>
              <div class="ledger-ym-nav">
                <button type="button" class="ym-nav-btn" @click="changeLedgerMonth(-1)">
                  <v-icon size="16">mdi-chevron-left</v-icon>
                </button>
                <div class="ledger-ym-display" @click="openLedgerMonthPicker">
                  <span class="ledger-ym-value">{{ currentYear }}.{{ currentMonth }}</span>
                  <input
                    type="month"
                    ref="ledgerMonthPickerRef"
                    :value="`${currentYear}-${currentMonth}`"
                    @change="onLedgerMonthPickerChange"
                    @click.stop
                    class="hidden-month-picker"
                  />
                </div>
                <button type="button" class="ym-nav-btn" @click="changeLedgerMonth(1)">
                  <v-icon size="16">mdi-chevron-right</v-icon>
                </button>
              </div>
            </div>

            <div class="approval-setup">
              <div class="sidebar-section-title">결재란</div>
              <div class="approval-count">
                <span class="approval-count-label">칸 수</span>
                <button
                  v-for="count in [1, 2, 3, 4]"
                  :key="count"
                  type="button"
                  class="approval-count-btn"
                  :class="{ active: approvalLabels.length === count }"
                  @click="setApprovalCount(count)"
                >
                  {{ count }}
                </button>
              </div>
              <div class="approval-fields">
                <label
                  v-for="(_, idx) in approvalLabels"
                  :key="idx"
                  class="approval-field"
                >
                  <span>{{ idx + 1 }}칸</span>
                  <input
                    v-model="approvalLabels[idx]"
                    type="text"
                    maxlength="8"
                    placeholder="직함"
                  />
                </label>
              </div>
              <MemberStampPanel :member-code="memberCode" />
            </div>

            <div class="ledger-detail-block">
              <div class="sidebar-section-title">세부내역</div>
              <p class="ledger-detail-hint">목차 보고서를 하나씩 확인할 수 있습니다.</p>
              <button
                v-for="report in detailReportList"
                :key="report.key"
                type="button"
                class="report-item report-item--detail"
                :class="{
                  active: selectedReport === report.key,
                  disabled: report.disabled,
                }"
                :disabled="report.disabled"
                @click="selectReport(report.key)"
              >
                <v-icon size="15" class="report-icon">{{ report.icon }}</v-icon>
                <div class="report-text">
                  <span class="report-title">{{ report.title }}</span>
                </div>
                <span v-if="report.disabled" class="report-badge">데이터 없음</span>
              </button>
              <p v-if="detailReportList.length === 0" class="ledger-detail-empty">
                표시할 세부 보고서가 없습니다.
              </p>
            </div>
          </template>
        </aside>

        <!-- 우측: 미리보기 -->
        <main class="reports-main">
          <div v-if="!selectedReport" class="reports-empty">
            <v-icon size="48" color="#cbd5e1">mdi-file-document-outline</v-icon>
            <p>회계분석보고서 또는 장부를 선택하세요</p>
          </div>

          <div v-else class="reports-pdf-panel">
            <div class="pdf-toolbar">
              <span class="pdf-title">{{ currentReportTitle }}</span>
              <div class="pdf-toolbar-actions">
                <a
                  v-if="activePdfPreviewUrl"
                  :href="activePdfPreviewUrl"
                  target="_blank"
                  rel="noopener"
                  class="btn-pdf-download ghost"
                >
                  <v-icon size="14">mdi-open-in-new</v-icon>
                  PDF 열기
                </a>
                <button
                  type="button"
                  class="btn-pdf-download"
                  :disabled="pdfLoading || (selectedReport !== 'accounting-analysis' && !activePdfBlob)"
                  @click="downloadPdf"
                >
                  <v-icon size="14">mdi-download</v-icon>
                  PDF 다운로드
                </button>
              </div>
            </div>
            <div v-if="pdfLoading" class="pdf-loading">
              <v-progress-circular indeterminate color="#1e3a5f" size="36" />
              <span>{{ loadingText }}</span>
            </div>
            <AccountingAnalysisPanel
              v-else-if="selectedReport === 'accounting-analysis' && analysisData"
              :data="analysisData"
              :member-code="memberCode"
              :year="currentYear"
              :month="String(analysisEndMonth)"
              :biz-gubun="bizGubun"
              :period-type="analysisPeriodType"
              :start-month="analysisStartMonth"
              :end-month="analysisEndMonth"
              @saved="loadAnalysis"
            />
            <iframe
              v-else-if="activePdfPreviewUrl && supportsInlinePdf"
              :src="activePdfPreviewUrl"
              class="pdf-iframe"
              title="보고서 미리보기"
            />
            <div v-else-if="activePdfPreviewUrl" class="pdf-mobile-fallback">
              <v-icon size="48" color="#1e3a5f">mdi-file-pdf-box</v-icon>
              <p>이 기기에서는 미리보기를 바로 띄울 수 없습니다.</p>
              <div class="fallback-actions">
                <a
                  :href="activePdfPreviewUrl"
                  target="_blank"
                  rel="noopener"
                  class="btn-pdf-download"
                >
                  <v-icon size="16">mdi-open-in-new</v-icon>
                  PDF 열기
                </a>
                <button type="button" class="btn-pdf-download ghost" @click="downloadPdf">
                  <v-icon size="16">mdi-download</v-icon>
                  다운로드
                </button>
              </div>
            </div>
            <div v-else class="reports-empty">
              <v-icon size="48" color="#cbd5e1">mdi-file-pdf-box</v-icon>
              <p>미리보기를 불러올 수 없습니다</p>
            </div>
          </div>
        </main>
      </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { usePageTabNav } from '@/composables/usePageTabNav';
import { bankbookApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import MemberStampPanel from '@/components/bankbook/MemberStampPanel.vue';
import AccountingAnalysisPanel from '@/components/bankbook/AccountingAnalysisPanel.vue';
import {
  buildAnalysisReferenceErrorHtml,
  buildAnalysisReferenceHtml,
  buildAnalysisReferenceLoadingHtml,
} from '@/components/bankbook/analysisReferencePopupHtml';

interface BeneficiaryMatch {
  cb_idx: number;
  cb_name: string;
  matchType: string;
}

interface ReportItem {
  key: string;
  title: string;
  description?: string;
  icon: string;
  disabled: boolean;
  pdfKey?: string;
}

const props = withDefaults(defineProps<{
  page?: boolean;
  memberCode: string;
  currentYear: string;
  currentMonth: string;
  bizGubun?: string;
  bankCode?: string | null;
  bankLabel?: string;
  cashbookList: any[];
  prevMonthBalance: number;
  incomeCodeList: { scode: string; sname: string }[];
  expenseCodeList: { scode: string; sname: string }[];
  beneficiaryMatches: Record<number, BeneficiaryMatch>;
}>(), {
  page: true,
});

const emit = defineEmits<{
  (e: 'change-year-month', year: string, month: string): void;
}>();

const selectedReport = ref('accounting-analysis');

const clampMonth = (value: number | string | undefined) => {
  const month = parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(month)) return 1;
  return Math.min(12, Math.max(1, month));
};

const analysisStartMonth = ref(clampMonth(props.currentMonth));
const analysisEndMonth = ref(clampMonth(props.currentMonth));
const awaitingEndMonth = ref(false);
const analysisMonthOptions = Array.from({ length: 12 }, (_, i) => ({
  month: i + 1,
  label: `${i + 1}월`,
}));

const analysisPeriodType = computed(() => {
  const start = analysisStartMonth.value;
  const end = analysisEndMonth.value;
  if (start === 1 && end === 6) return 'h1';
  if (start === 7 && end === 12) return 'h2';
  if (start === end) return 'month';
  return 'range';
});

const analysisRange = computed(() => {
  const startMonth = analysisStartMonth.value;
  const endMonth = analysisEndMonth.value;
  let periodName = `${startMonth}~${endMonth}월`;
  if (startMonth === endMonth) periodName = `${startMonth}월`;
  else if (startMonth === 1 && endMonth === 6) periodName = '상반기';
  else if (startMonth === 7 && endMonth === 12) periodName = '하반기';
  else if (startMonth === 1 && endMonth === 12) periodName = '연간';
  return {
    startMonth,
    endMonth,
    label: `${props.currentYear}년 ${periodName}`,
  };
});

const analysisPeriodHint = computed(() => analysisRange.value.label);

const normalizeAnalysisRange = () => {
  const start = clampMonth(analysisStartMonth.value);
  let end = clampMonth(analysisEndMonth.value);
  if (end < start) end = start;
  analysisStartMonth.value = start;
  analysisEndMonth.value = end;
};

const monthButtonClass = (month: number) => {
  const start = analysisStartMonth.value;
  const end = analysisEndMonth.value;
  return {
    active: month === start || month === end,
    'in-range': month > start && month < end,
    'is-pending': awaitingEndMonth.value && month === start,
  };
};

const pickAnalysisMonth = (month: number) => {
  const next = clampMonth(month);
  if (!awaitingEndMonth.value) {
    analysisStartMonth.value = next;
    analysisEndMonth.value = next;
    awaitingEndMonth.value = true;
    return;
  }
  if (next < analysisStartMonth.value) {
    analysisEndMonth.value = analysisStartMonth.value;
    analysisStartMonth.value = next;
  } else {
    analysisEndMonth.value = next;
  }
  awaitingEndMonth.value = false;
};

const resetAnalysisMonths = (month: number | string | undefined) => {
  const next = clampMonth(month);
  analysisStartMonth.value = next;
  analysisEndMonth.value = next;
  awaitingEndMonth.value = false;
};

const onRangeSelectChange = () => {
  awaitingEndMonth.value = false;
  normalizeAnalysisRange();
};

const ledgerMonthPickerRef = ref<HTMLInputElement | null>(null);

const emitYearMonth = (year: number, month: number) => {
  const y = Math.min(2100, Math.max(2000, year));
  const m = clampMonth(month);
  emit('change-year-month', String(y), String(m).padStart(2, '0'));
};

const changeReportYear = (delta: number) => {
  const year = parseInt(props.currentYear, 10) + delta;
  const month = clampMonth(props.currentMonth);
  emitYearMonth(year, month);
};

const changeLedgerMonth = (delta: number) => {
  let year = parseInt(props.currentYear, 10);
  let month = clampMonth(props.currentMonth) + delta;
  if (month < 1) {
    month = 12;
    year -= 1;
  } else if (month > 12) {
    month = 1;
    year += 1;
  }
  emitYearMonth(year, month);
};

const openLedgerMonthPicker = () => {
  const el = ledgerMonthPickerRef.value;
  if (!el) return;
  try {
    if (typeof el.showPicker === 'function') {
      el.showPicker();
      return;
    }
  } catch {
    // showPicker는 사용자 제스처 밖이면 NotAllowedError → click 폴백
  }
  el.focus();
  el.click();
};

const onLedgerMonthPickerChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  if (!value || !value.includes('-')) return;
  const [yearStr, monthStr] = value.split('-');
  emitYearMonth(parseInt(yearStr, 10), parseInt(monthStr, 10));
};

const LEDGER_DETAIL_KEYS = [
  'income-settlement',
  'expense-settlement',
  'cash-journal',
  'general-ledger',
  'income-resolution',
  'expense-resolution',
  'return-resolution',
  'subsidy-statement',
  'subsidy-income-resolution',
  'subsidy-expense-resolution',
  'donation-statement',
  'donation-income-resolution',
  'donation-expense-resolution',
] as const;
const APPROVAL_PRESETS: Record<number, string[]> = {
  1: ['시설장'],
  2: ['담당자', '시설장'],
  3: ['담당자', '팀장', '시설장'],
  4: ['담당자', '팀장', '부장', '시설장'],
};

const approvalLabels = ref<string[]>([...APPROVAL_PRESETS[1]]);
const pdfLoading = ref(false);
const referencePopupLoading = ref(false);
const pdfPreviewUrl = ref<string | null>(null);
const pdfBlob = ref<Blob | null>(null);
/** 현재 blob/url이 어떤 보고서 PDF인지 (늦게 끝난 장부 요청이 덮어쓰는 것 방지) */
const pdfSourceKey = ref<string | null>(null);
/** 회계분석 PDF가 어떤 기간으로 생성됐는지 (기간 불일치 시 강제 재생성) */
const analysisPdfPeriodKey = ref<string | null>(null);
let pdfRequestSeq = 0;
const analysisData = ref<Record<string, any> | null>(null);

const currentAnalysisPeriodKey = computed(
  () => `${props.currentYear}:${clampMonth(analysisStartMonth.value)}-${clampMonth(analysisEndMonth.value)}`
);

const reportList = ref<ReportItem[]>([
  { key: 'accounting-analysis', title: '회계분석보고서', description: '인건비·생계비·차입 점검', icon: 'mdi-chart-timeline-variant', disabled: false, pdfKey: 'accounting-analysis' },
  { key: 'ledger-book', title: '장부', description: '표지·목차 + 보고서 이어붙임', icon: 'mdi-book-open-variant', disabled: false, pdfKey: 'ledger-book' },
  { key: 'income-settlement', title: '세입결산서', description: '관·항·목 예산/결산', icon: 'mdi-chart-box-outline', disabled: false, pdfKey: 'income-settlement' },
  { key: 'expense-settlement', title: '세출결산서', description: '관·항·목 예산/결산', icon: 'mdi-chart-box-plus-outline', disabled: false, pdfKey: 'expense-settlement' },
  { key: 'cash-journal', title: '현금출납부', description: '전표·입출금·잔액', icon: 'mdi-cash-register', disabled: false, pdfKey: 'cash-journal' },
  { key: 'general-ledger', title: '총계정원장', description: '계정별 수입·지출·차인', icon: 'mdi-book-open-page-variant', disabled: false, pdfKey: 'general-ledger' },
  { key: 'income-resolution', title: '수입결의서', description: '수입 결의 내역', icon: 'mdi-arrow-down-bold-box-outline', disabled: false, pdfKey: 'income-resolution' },
  { key: 'expense-resolution', title: '지출결의서', description: '지출 결의 내역', icon: 'mdi-arrow-up-bold-box-outline', disabled: false, pdfKey: 'expense-resolution' },
  { key: 'return-resolution', title: '반납결의서', description: '반납 결의 내역', icon: 'mdi-backup-restore', disabled: false, pdfKey: 'return-resolution' },
  { key: 'subsidy-statement', title: '정부보조금명세서', description: '보조금 수령 내역', icon: 'mdi-domain', disabled: false, pdfKey: 'subsidy-statement' },
  { key: 'subsidy-income-resolution', title: '보조금 수입결의서', description: '보조금 수입 결의', icon: 'mdi-arrow-down-bold-box-outline', disabled: false, pdfKey: 'subsidy-income-resolution' },
  { key: 'subsidy-expense-resolution', title: '보조금 지출결의서', description: '보조금 지출 결의', icon: 'mdi-arrow-up-bold-box-outline', disabled: false, pdfKey: 'subsidy-expense-resolution' },
  { key: 'donation-statement', title: '후원금내역서', description: '후원금 수입·지출', icon: 'mdi-hand-heart-outline', disabled: false, pdfKey: 'donation-statement' },
  { key: 'donation-income-resolution', title: '후원금 수입결의서', description: '후원금 수입 결의', icon: 'mdi-arrow-down-bold-box-outline', disabled: false, pdfKey: 'donation-income-resolution' },
  { key: 'donation-expense-resolution', title: '후원금 지출결의서', description: '후원금 지출 결의', icon: 'mdi-arrow-up-bold-box-outline', disabled: false, pdfKey: 'donation-expense-resolution' },
]);

const detailReportList = computed(() =>
  LEDGER_DETAIL_KEYS
    .map((key) => reportList.value.find((report) => report.key === key))
    .filter((report): report is ReportItem => !!report)
);

const isDetailReport = (reportKey: string) =>
  (LEDGER_DETAIL_KEYS as readonly string[]).includes(reportKey);

const isLedgerWorkspace = computed(
  () => selectedReport.value === 'ledger-book' || isDetailReport(selectedReport.value)
);

const isAnalysisWorkspace = computed(
  () => selectedReport.value === 'accounting-analysis'
);

const supportsInlinePdf = computed(() => {
  if (typeof navigator === 'undefined') return true;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
  if (iOS) return false;
  if (typeof navigator.pdfViewerEnabled === 'boolean') return navigator.pdfViewerEnabled;
  return true;
});

const loadingText = computed(() => {
  if (selectedReport.value === 'ledger-book') return '장부 생성 중...';
  if (selectedReport.value === 'accounting-analysis') return '분석보고서 불러오는 중...';
  return 'PDF 생성 중...';
});

const currentReportTitle = computed(() => {
  return reportList.value.find((r) => r.key === selectedReport.value)?.title || '';
});

const currentPdfKey = computed(() => {
  const report = reportList.value.find((r) => r.key === selectedReport.value);
  return report?.pdfKey || selectedReport.value;
});

const activePdfPreviewUrl = computed(() => (
  pdfSourceKey.value === currentPdfKey.value ? pdfPreviewUrl.value : null
));

const activePdfBlob = computed(() => (
  pdfSourceKey.value === currentPdfKey.value ? pdfBlob.value : null
));

const revokePdfUrl = () => {
  if (pdfPreviewUrl.value) {
    URL.revokeObjectURL(pdfPreviewUrl.value);
    pdfPreviewUrl.value = null;
  }
  pdfBlob.value = null;
  pdfSourceKey.value = null;
  analysisPdfPeriodKey.value = null;
  analysisData.value = null;
};

const invalidateAnalysisPdfCache = () => {
  if (pdfSourceKey.value === 'accounting-analysis') {
    if (pdfPreviewUrl.value) {
      URL.revokeObjectURL(pdfPreviewUrl.value);
      pdfPreviewUrl.value = null;
    }
    pdfBlob.value = null;
    pdfSourceKey.value = null;
  }
  analysisPdfPeriodKey.value = null;
};

const loadAvailability = async () => {
  if (!props.memberCode) return;
  try {
    const res = await bankbookApi.getReportAvailability(props.memberCode, {
      year: props.currentYear,
      month: props.currentMonth,
      bizGubun: props.bizGubun,
      bankCode: props.bankCode || undefined,
    });
    const data = res?.data?.data || res?.data || {};
    const sections = (data.sections || {}) as Record<string, boolean>;
    // 장부 목차(hasLedgerSectionContent)와 동일 기준 — sections 우선
    const flags: Record<string, boolean> = {
      'ledger-book': true,
      'accounting-analysis': true,
      'income-settlement': sections['income-settlement'] ?? Boolean(data.hasIncome || data.hasAny),
      'expense-settlement': sections['expense-settlement'] ?? Boolean(data.hasExpense || data.hasAny),
      'cash-journal': sections['cash-journal'] ?? Boolean(data.hasAny),
      'general-ledger': sections['general-ledger'] ?? Boolean(data.hasAny),
      'income-resolution': sections['income-resolution'] ?? Boolean(data.hasIncome),
      'expense-resolution': sections['expense-resolution'] ?? Boolean(data.hasExpense),
      'return-resolution': sections['return-resolution'] ?? Boolean(data.hasReturn),
      'subsidy-statement': sections['subsidy-statement'] ?? Boolean(data.hasSubsidyIncome || data.hasSubsidy),
      'subsidy-income-resolution': sections['subsidy-income-resolution'] ?? Boolean(data.hasSubsidyIncome),
      'subsidy-expense-resolution': sections['subsidy-expense-resolution'] ?? Boolean(data.hasSubsidyExpense),
      'donation-statement': sections['donation-statement'] ?? Boolean(data.hasDonation),
      'donation-income-resolution': sections['donation-income-resolution'] ?? Boolean(data.hasDonationIncome),
      'donation-expense-resolution': sections['donation-expense-resolution'] ?? Boolean(data.hasDonationExpense),
    };
    reportList.value = reportList.value.map((report) => ({
      ...report,
      disabled: flags[report.key] === false,
    }));
  } catch (error: any) {
    console.error('보고서 데이터 확인 오류:', error);
    reportList.value = reportList.value.map((report) => ({ ...report, disabled: false }));
  }
};

const currentApprovalLabels = () => approvalLabels.value.map((label) => label.trim());

const setApprovalCount = (count: number) => {
  const preset = APPROVAL_PRESETS[count] || APPROVAL_PRESETS[1];
  const current = currentApprovalLabels();
  const onlyDefault = current.length === 1 && current[0] === '시설장';
  if (onlyDefault) {
    approvalLabels.value = [...preset];
    return;
  }
  const next = current.slice(0, count);
  while (next.length < count) {
    next.push(preset[next.length] || '');
  }
  approvalLabels.value = next;
};

let skipApprovalPersist = false;
let approvalPersistTimer: ReturnType<typeof setTimeout> | null = null;

const persistApprovalLabels = async () => {
  const labels = currentApprovalLabels();
  if (!props.memberCode) return;
  try {
    await bankbookApi.upsertFinanceInfo(props.memberCode, {
      approval_labels: labels.some(Boolean) ? labels.join(',') : '시설장',
    });
  } catch (error: any) {
    console.error('결재란 저장 오류:', error);
  }
};

const refreshSelectedReport = async () => {
  const report = reportList.value.find((r) => r.key === selectedReport.value);
  if (report?.key === 'accounting-analysis') {
    await loadAnalysis();
    return;
  }
  if (report?.pdfKey) {
    await generatePdf(report.pdfKey);
  }
};

const scheduleApprovalChange = () => {
  if (skipApprovalPersist) return;
  if (approvalPersistTimer) clearTimeout(approvalPersistTimer);
  approvalPersistTimer = setTimeout(async () => {
    await persistApprovalLabels();
    await refreshSelectedReport();
  }, 400);
};

const loadApprovalLabels = async () => {
  if (!props.memberCode) return;
  skipApprovalPersist = true;
  try {
    const res = await bankbookApi.getFinanceInfo(props.memberCode);
    const raw = String(res.data?.data?.approval_labels || '');
    const labels = raw.split(',').map((item) => item.trim());
    approvalLabels.value = labels.some(Boolean) ? labels : ['시설장'];
  } catch {
    approvalLabels.value = ['시설장'];
  } finally {
    skipApprovalPersist = false;
  }
};

const analysisPeriodParams = () => {
  const start = clampMonth(analysisStartMonth.value);
  const end = clampMonth(analysisEndMonth.value);
  return {
    year: props.currentYear,
    month: String(end).padStart(2, '0'),
    bizGubun: props.bizGubun,
    bankCode: props.bankCode || undefined,
    periodType: analysisPeriodType.value,
    startMonth: start,
    endMonth: end,
  };
};

const loadAnalysis = async () => {
  if (!props.memberCode) return;
  persistApprovalLabels();
  analysisData.value = null;
  pdfLoading.value = true;
  try {
    const response = await bankbookApi.getAccountingAnalysis(
      props.memberCode,
      analysisPeriodParams()
    );
    analysisData.value = response.data?.data || null;
  } catch (error: any) {
    analysisData.value = null;
    alert(`분석보고서 조회 실패: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
  } finally {
    pdfLoading.value = false;
  }
};

const generatePdf = async (reportKey: string) => {
  if (!props.memberCode) return;

  persistApprovalLabels();
  const requestId = ++pdfRequestSeq;
  const periodKeyForRequest = reportKey === 'accounting-analysis'
    ? currentAnalysisPeriodKey.value
    : null;
  if (pdfPreviewUrl.value) {
    URL.revokeObjectURL(pdfPreviewUrl.value);
    pdfPreviewUrl.value = null;
  }
  pdfBlob.value = null;
  pdfSourceKey.value = null;
  if (reportKey === 'accounting-analysis') {
    analysisPdfPeriodKey.value = null;
  }
  pdfLoading.value = true;

  try {
    const analysisParams = reportKey === 'accounting-analysis'
      ? analysisPeriodParams()
      : null;
    const response = await bankbookApi.getResolutionReportPdf(
      props.memberCode,
      reportKey,
      {
        year: props.currentYear,
        month: analysisParams
          ? analysisParams.month
          : props.currentMonth,
        bizGubun: props.bizGubun,
        bankCode: props.bankCode || undefined,
        approvalLabels: currentApprovalLabels().join(',') || '시설장',
        ...(analysisParams
          ? {
              periodType: analysisParams.periodType,
              startMonth: analysisParams.startMonth,
              endMonth: analysisParams.endMonth,
            }
          : {}),
      }
    );

    // 이전에 시작한 장부 등 요청이 늦게 끝나면 무시
    if (requestId !== pdfRequestSeq) return;
    // 생성 중 기간이 바뀌었으면 이 PDF는 버림
    if (
      reportKey === 'accounting-analysis'
      && periodKeyForRequest !== currentAnalysisPeriodKey.value
    ) {
      return;
    }

    pdfBlob.value = new Blob([response.data], { type: 'application/pdf' });
    pdfPreviewUrl.value = URL.createObjectURL(pdfBlob.value);
    pdfSourceKey.value = reportKey;
    analysisPdfPeriodKey.value = reportKey === 'accounting-analysis'
      ? periodKeyForRequest
      : null;
  } catch (error: any) {
    if (requestId !== pdfRequestSeq) return;

    let errorMessage = '알 수 없는 오류';
    const status = error.response?.status || '';

    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        errorMessage = json.message || errorMessage;
      } catch {
        errorMessage = error.message || errorMessage;
      }
    } else {
      errorMessage = error.response?.data?.message || error.message || errorMessage;
    }

    alert(`PDF 생성 실패: ${errorMessage}${status ? ` (${status})` : ''}`);
  } finally {
    if (requestId === pdfRequestSeq) {
      pdfLoading.value = false;
    }
  }
};

const selectReport = async (reportKey: string) => {
  const report = reportList.value.find((r) => r.key === reportKey);
  if (!report || report.disabled) return;

  if (selectedReport.value === reportKey) return;
  selectedReport.value = reportKey;
  // 진행 중/잔존 PDF가 다른 보고서로 쓰이지 않게 무효화
  pdfRequestSeq += 1;
  revokePdfUrl();

  if (reportKey === 'accounting-analysis') {
    await loadAnalysis();
    return;
  }

  if (report.pdfKey) {
    await generatePdf(report.pdfKey);
  }
};

const reportPageTabs = computed(() => {
  if (!props.page) return [];
  return reportList.value.map((report) => ({
    key: report.key,
    title: report.title,
    disabled: report.disabled,
  }));
});
usePageTabNav('accounting-reports', reportPageTabs, selectedReport, (key) => {
  void selectReport(key);
});

const writePopupHtml = (popup: Window, html: string) => {
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
};

const openAnalysisReferencePopup = async () => {
  if (!props.memberCode || referencePopupLoading.value) return;

  const width = Math.min(1100, screen.availWidth - 40);
  const height = Math.min(900, screen.availHeight - 40);
  const left = Math.max(0, Math.round((screen.availWidth - width) / 2));
  const top = Math.max(0, Math.round((screen.availHeight - height) / 2));
  const popup = window.open(
    'about:blank',
    'caion-accounting-analysis-reference',
    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
  );
  if (!popup) {
    alert('팝업이 차단되었습니다. 브라우저에서 팝업을 허용한 뒤 다시 시도해 주세요.');
    return;
  }

  writePopupHtml(popup, buildAnalysisReferenceLoadingHtml());
  referencePopupLoading.value = true;
  try {
    const response = await bankbookApi.getAccountingAnalysisReference(
      props.memberCode,
      analysisPeriodParams()
    );
    if (popup.closed) return;
    const data = response.data?.data;
    if (!data) {
      writePopupHtml(popup, buildAnalysisReferenceErrorHtml('계정별내역 데이터가 없습니다.'));
      return;
    }
    writePopupHtml(popup, buildAnalysisReferenceHtml(data));
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || '알 수 없는 오류';
    if (!popup.closed) {
      writePopupHtml(popup, buildAnalysisReferenceErrorHtml(`계정별내역 조회 실패: ${errorMessage}`));
    }
    alert(`계정별내역 조회 실패: ${errorMessage}`);
  } finally {
    referencePopupLoading.value = false;
  }
};

const downloadPdf = async () => {
  const pdfKey = currentPdfKey.value;
  if (!pdfKey) return;

  // 회계분석은 기간 변경·캐시 꼬임을 막기 위해 항상 현재 기간으로 재생성
  const forceRefresh = pdfKey === 'accounting-analysis'
    || pdfSourceKey.value !== pdfKey
    || !pdfBlob.value
    || (
      pdfKey === 'accounting-analysis'
      && analysisPdfPeriodKey.value !== currentAnalysisPeriodKey.value
    );

  if (forceRefresh) {
    await generatePdf(pdfKey);
  }
  if (pdfSourceKey.value !== pdfKey || !pdfBlob.value) return;
  if (
    pdfKey === 'accounting-analysis'
    && analysisPdfPeriodKey.value !== currentAnalysisPeriodKey.value
  ) {
    return;
  }

  const url = URL.createObjectURL(pdfBlob.value);
  const link = document.createElement('a');
  link.href = url;
  const periodName = pdfKey === 'accounting-analysis'
    ? analysisRange.value.label
    : `${props.currentYear}년${props.currentMonth}월`;
  link.download = `${currentReportTitle.value}_${periodName}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

onMounted(() => {
  loadApprovalLabels();
});

watch(() => props.memberCode, () => {
  loadApprovalLabels();
});

watch(approvalLabels, () => {
  scheduleApprovalChange();
}, { deep: true });

watch(
  [analysisStartMonth, analysisEndMonth],
  () => {
    const start = clampMonth(analysisStartMonth.value);
    const end = clampMonth(analysisEndMonth.value);
    if (analysisStartMonth.value !== start) analysisStartMonth.value = start;
    if (end < start) {
      if (analysisEndMonth.value !== start) analysisEndMonth.value = start;
      return;
    }
    if (analysisEndMonth.value !== end) analysisEndMonth.value = end;

    // 기간이 바뀌면 이전 PDF(예: 1월 단월)가 남지 않게 무효화
    invalidateAnalysisPdfCache();

    if (selectedReport.value === 'accounting-analysis') {
      loadAnalysis();
    }
  }
);

// 연도 변경 시에만 분석기간을 통장월로 리셋
watch(
  () => props.currentYear,
  () => {
    resetAnalysisMonths(props.currentMonth);
  }
);

// 통장월 변경: 단월 선택 중일 때만 동기화 (1~6 등 확장 기간은 유지)
watch(
  () => props.currentMonth,
  (month) => {
    if (analysisStartMonth.value === analysisEndMonth.value) {
      resetAnalysisMonths(month);
    }
  }
);

watch(
  () => [props.memberCode, props.currentYear, props.currentMonth, props.bizGubun, props.bankCode],
  async () => {
    await loadAvailability();
    if (!selectedReport.value) {
      selectedReport.value = 'accounting-analysis';
    }
    let report = reportList.value.find((r) => r.key === selectedReport.value);
    if (report?.disabled) {
      selectedReport.value = 'accounting-analysis';
      report = reportList.value.find((r) => r.key === 'accounting-analysis');
      revokePdfUrl();
    }
    if (report?.key === 'accounting-analysis') {
      await loadAnalysis();
      return;
    }
    if (report?.pdfKey) {
      await generatePdf(report.pdfKey);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (approvalPersistTimer) clearTimeout(approvalPersistTimer);
  revokePdfUrl();
});
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/bankbook-reports';
</style>
